import { TypedDocumentNode, gql } from '@apollo/client';
import { isPast } from 'date-fns';

import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';

import type { useFilterLivePrimaryBuys_anyCard } from './__generated__/useFilterLivePrimaryBuys.graphql';

export const useFilterLivePrimaryBuys = <
  T extends useFilterLivePrimaryBuys_anyCard,
>(
  cards: T[]
) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  return cards
    .map(card => {
      const { livePrimaryOffer, user } = card;
      const endDate = livePrimaryOffer?.endDate;
      const price = livePrimaryOffer?.price;

      const ended = endDate && isPast(endDate);

      // Checking the token has an owner is mandatory as the
      // PrimaryOffer can ends way after the token has been sold
      const isOnSale = !!livePrimaryOffer && !user && !ended;

      const isPrimaryBuy = livePrimaryOffer?.cards.length === 1;

      if (isOnSale && price && endDate && isPrimaryBuy) {
        return {
          endDate,
          ended,
          monetaryAmount: toMonetaryAmount(price),
          card,
        };
      }
      return undefined;
    })
    .filter(Boolean);
};

useFilterLivePrimaryBuys.fragments = {
  anyCard: gql`
    fragment useFilterLivePrimaryBuys_anyCard on AnyCardInterface {
      slug
      livePrimaryOffer {
        id
        price {
          ...useMonetaryAmount_monetaryAmount
        }
        endDate
        cards: anyCards {
          slug
        }
      }
      user {
        slug
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useFilterLivePrimaryBuys_anyCard>,
};
